import dayjs from 'dayjs';
import { comparer, computed, type IComputedValue, makeAutoObservable, observable, runInAction } from 'mobx';
import { HolidayType } from '../../components/Holidays/graphql';
import {
  GridDateType,
  Item,
  isCoexistableTeachingBlockCard,
  PlacedTeachingBlockCardType,
  RoomItem,
  RoomTypes,
  RowItem,
  TeachingBlockCardType,
  TeachingBlockVirtualCard,
} from '../../components/TeachingBlockGrid/types';
import {
  _ClassesQuery,
  _PeopleQuery,
  _RoomsQuery,
  _SubjectsQuery,
  PlaceTeachingBlockCardDocument,
  PlaceTeachingBlockCardMutation,
  PlaceTeachingBlockCardMutationVariables,
  SetTeachingBlockCardRoomsDocument,
  SetTeachingBlockCardRoomsMutation,
  SetTeachingBlockCardRoomsMutationVariables,
  SetTeachingBlockCardsLockedDocument,
  SetTeachingBlockCardsLockedMutation,
  SetTeachingBlockCardsLockedMutationVariables,
  UnplaceTeachingBlockCardsDocument,
  UnplaceTeachingBlockCardsMutation,
  UnplaceTeachingBlockCardsMutationVariables,
} from '../../types/planung-graphql-client-defs';
import { uniqueBy } from '../../utils/arrayFunc';
import {
  getDaysDifference,
  isFirstAfterSecond,
  isFirstBeforeSecond,
  isFirstBetweenSecondAndThird,
} from '../../utils/dateCalculations';
import { WarningType } from '../../utils/getWarningTypeTranslation';
import { sortRowsByBuilding } from '../../utils/roomLabel';
import { sortClasses } from '../../utils/sortHelper';
import { urqlClient } from '../../utils/urqlClient';
import { AvailabilityCardType, AvailabilityDayType, ClassDivisionGroupFormat } from '../DayAvailabilityStore';
import type { TeachingBlockStore } from './TeachingBlockStore';

// Stabile Leer-Referenzen: leere Row-Weeks liefern immer dieselbe Array-Instanz, damit
// `comparer.shallow` sie als unverändert erkennt und keinen Rerender auslöst.
const EMPTY_PLACED_CARDS: PlacedTeachingBlockCardType[] = [];
const EMPTY_VIRTUAL_CARDS: TeachingBlockVirtualCard[] = [];

/**
 * Datenschicht der TeachingBlock-Planung (Phase 7.1).
 * Hält Kartendaten, datumsbasierte Platzierung, Räume und das Ferien-Splitting.
 * UI-State (pickedCard, Modals, Selection) liegt weiterhin auf der Fassade
 * `TeachingBlockStore` und wird über `this.root.*` angesprochen.
 */
export class TeachingBlockCardStore {
  private _cards: TeachingBlockCardType[] = [];
  /** O(1)-Index uuid → Karte (Perf, Fix A); manuell synchron zu `_cards` gehalten. */
  private _cardsByUuid: Map<string, TeachingBlockCardType> = new Map();
  private loading: boolean = false;

  public freeCards: TeachingBlockCardType[] = [];
  public placedCards: PlacedTeachingBlockCardType[] = [];
  public virtualCards: TeachingBlockVirtualCard[] = [];

  public cardRooms: Map<string, Map<RoomTypes, RoomItem[]>> = new Map();

  public teachers: Map<string, Pick<_PeopleQuery, 'people'>['people'][0]> = new Map();
  public classes: Map<string, Pick<_ClassesQuery, 'classes'>['classes'][0]> = new Map();
  public subjects: Map<string, Pick<_SubjectsQuery, 'subjects'>['subjects'][0]> = new Map();
  public rooms: Map<string, Pick<_RoomsQuery, 'rooms'>['rooms'][0]> = new Map();

  private _classesRows: RowItem[] = [];
  private _teachersRows: RowItem[] = [];
  private _roomsRows: RowItem[] = [];

  /**
   * Perf: per-Key memoisierte Sichten auf {@link placedCardsByRowWeek} /
   * {@link virtualCardsByRowWeek} mit `comparer.shallow`. Dadurch rendert beim
   * Platzieren/Verschieben nur die Row-Week neu, deren Karten-Set sich tatsächlich
   * ändert – nicht alle GridWeeks (die ganze computed-Map ändert bei jeder Mutation).
   * `#`-private (kein MobX-Wrapping – MobX ignoriert `#`-Felder); halten je `indexKey`
   * eine `computed`-Instanz.
   */
  /** Ausstehende Ferien-Splits ohne Speichern; s. `splitTimeConflict`. */
  #pendingUnsavedSplits = 0;

  #placedCardsForRowWeekCache = new Map<string, IComputedValue<PlacedTeachingBlockCardType[]>>();
  #virtualCardsForRowWeekCache = new Map<string, IComputedValue<TeachingBlockVirtualCard[]>>();

  constructor(
    private root: TeachingBlockStore,
    initialCards: TeachingBlockCardType[],
  ) {
    this._cards = initialCards;
    this._indexCards();

    makeAutoObservable(this, {
      freeCards: observable.shallow,
      // Keine MobX-Action: die Methoden müssen die inneren computeds beim Aufruf im
      // Render tracken (eine Action würde untracken). Die Caches sind `#`-private und
      // damit für MobX ohnehin unsichtbar.
      placedCardsForRowWeek: false,
      virtualCardsForRowWeek: false,
    });
  }

  /** Baut den uuid→Karte-Index neu auf. Nach jeder `_cards`-Zuweisung aufrufen. */
  private _indexCards() {
    this._cardsByUuid = new Map(this._cards.map((card) => [card.uuid, card]));
  }

  // ─── Stammdaten ───────────────────────────────────────────────────────────

  public loadData({
    classes,
    rooms,
    subjects,
    teachers,
  }: {
    teachers: Pick<_PeopleQuery, 'people'>['people'];
    classes: Pick<_ClassesQuery, 'classes'>['classes'];
    rooms: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'];
  }) {
    this.setClasses(classes);
    this.setRooms(rooms);
    this.setSubjects(subjects);
    this.setTeachers(teachers);
  }

  public setClasses(classes: Pick<_ClassesQuery, 'classes'>['classes']) {
    this.classes = new Map(classes.map((c) => [c.uuid, c]));
  }

  public setRooms(rooms: Pick<_RoomsQuery, 'rooms'>['rooms']) {
    this.rooms = new Map(rooms.map((room) => [room.uuid, room]));
  }

  public setSubjects(subjects: Pick<_SubjectsQuery, 'subjects'>['subjects']) {
    this.subjects = new Map(subjects.map((subject) => [subject.uuid, subject]));
  }

  public setTeachers(teachers: Pick<_PeopleQuery, 'people'>['people']) {
    this.teachers = new Map(teachers.map((teacher) => [teacher.uuid, teacher]));
  }

  public addClasses(classes: Pick<_ClassesQuery, 'classes'>['classes']) {
    classes.forEach((c) => this.classes.set(c.uuid, c));
  }

  public removeClasses(classesUuids: string[]) {
    classesUuids.forEach((uuid) => this.classes.delete(uuid));
  }

  public removeTeachers(teacherUuids: string[]) {
    teacherUuids.forEach((uuid) => this.teachers.delete(uuid));
  }

  public removeSubjects(subjectUuids: string[]) {
    subjectUuids.forEach((uuid) => this.subjects.delete(uuid));
  }

  public removeRooms(roomUuids: string[]) {
    roomUuids.forEach((uuid) => this.rooms.delete(uuid));
  }

  // ─── Rows ─────────────────────────────────────────────────────────────────

  public getRows(): RowItem[] {
    if (this.root.gridContext === 'teachers') return this._teachersRows;
    if (this.root.gridContext === 'rooms')
      return this.root.groupRoomsByBuilding ? sortRowsByBuilding(this._roomsRows) : this._roomsRows;
    return this._classesRows;
  }

  // ─── Init / Laden ─────────────────────────────────────────────────────────

  public _loadCards() {
    if (this.loading) {
      return;
    }
    this.loading = true;

    // map rows
    const classesRows =
      this.root.currentVersion?.classesConnection.edges
        .sort((a, b) => sortClasses(a.node, b.node))
        .map((c) => ({
          label: c.node.shortName,
          value: c.node.uuid,
        })) ?? [];

    const teachersRows =
      this.root.currentVersion?.teachersConnection.edges.map((t) => ({
        label: t.node.displayNameShort ?? t.node.shortName,
        value: t.node.uuid,
      })) ?? [];

    const roomsRows =
      this.root.currentVersion?.roomsConnection.edges.map((t) => ({
        label: t.node.name,
        value: t.node.uuid,
        subLabel: t.node.building?.name,
      })) ?? [];

    runInAction(() => {
      const errorCards = this._cards.filter((card) => {
        const startDateString = dayjs(card.startDate).format('YYYY-MM-DD');
        const endDateString = dayjs(card.endDate).format('YYYY-MM-DD');
        return (
          card.startDate &&
          card.endDate &&
          (!this.root.dates.get(startDateString) || !this.root.dates.get(endDateString))
        );
      });
      if (errorCards.length > 0) console.warn(errorCards);

      this.freeCards = this._cards.filter((card) => !card.startDate || !card.endDate);
      this.freeCards.push(...errorCards);
      this.placedCards = this._cards
        .filter((card) => card.startDate && card.endDate)
        .filter((card) => !errorCards.some((ec) => ec.uuid === card.uuid))
        .map((c) => c as PlacedTeachingBlockCardType);

      // Veraltete Platzierungen leeren, bevor der aktuelle Stand neu eingetragen
      // wird (verhindert leakende Phantom-Konflikte ohne Page-Reload). Vorbild:
      // CardStore.initCards → getTimeAvailabilityStore()?.reset().
      this.root.getAvailabilityStore()?.resetPlaced();

      this.placedCards.forEach((card) => {
        this.root.getAvailabilityStore()?.setCardsResourcesPlaced([this.cardToAvailabilityCard(card)], {
          start: card.startDate,
          end: card.endDate,
        });
      });

      this._classesRows = classesRows;
      this._teachersRows = teachersRows;
      this._roomsRows = roomsRows;
      // Kontext beibehalten; der gespeicherte Wert wird nach dem Laden wiederhergestellt.
      this.root.setContext(this.root.uiStore.gridContext);
      this._findSplits();
      this.root.conflictStore.setConflicts();
    });

    this.loading = false;
  }

  // ─── Verfügbarkeits-Karte ─────────────────────────────────────────────────

  public cardToAvailabilityCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    const classDivisionGroups: ClassDivisionGroupFormat[] = [];

    card.lessonClasses.forEach((lc) => {
      if (lc.groups.length !== 0) {
        lc.groups.forEach((g) => {
          classDivisionGroups.push(
            `${card.uuid}:${lc.class.uuid}:${lc.usedDivision?.uuid ?? 'noDivision'}:${g.uuid}` as ClassDivisionGroupFormat,
          );
        });
      } else {
        classDivisionGroups.push(`${card.uuid}:${lc.class.uuid}:noDivision:noGroup` as ClassDivisionGroupFormat);
      }
    });

    return {
      classes: card.classes.map((c) => c.uuid),
      teachers: card.teachers.map((t) => t.uuid),
      rooms: card.rooms.map((r) => r.uuid),
      subject: card.subject?.uuid ?? '',
      uuid: card.uuid,
      classDivisionGroups,
    };
  }

  public getAvailabilityCardForPickedCard() {
    if (!this.root.pickedCard?.card) return undefined;
    const card = this.root.pickedCard.card;
    return this.cardToAvailabilityCard(card);
  }

  // ─── Lock ─────────────────────────────────────────────────────────────────

  public lockCard(card: TeachingBlockCardType) {
    runInAction(() => {
      card.locked = true;
    });

    void urqlClient
      .mutation<SetTeachingBlockCardsLockedMutation, SetTeachingBlockCardsLockedMutationVariables>(
        SetTeachingBlockCardsLockedDocument,
        { cardUuids: [card.uuid], locked: true },
      )
      .toPromise();
  }

  public unlockCard(card: TeachingBlockCardType) {
    runInAction(() => {
      card.locked = false;
    });

    void urqlClient
      .mutation<SetTeachingBlockCardsLockedMutation, SetTeachingBlockCardsLockedMutationVariables>(
        SetTeachingBlockCardsLockedDocument,
        { cardUuids: [card.uuid], locked: false },
      )
      .toPromise();
  }

  // ─── Platzierung ──────────────────────────────────────────────────────────

  public discardCard(card: TeachingBlockCardType | PlacedTeachingBlockCardType) {
    if (card.locked) return;

    if (card.startDate && card.endDate) {
      this.root.getAvailabilityStore()?.removeCardsResourcesPlaced([this.cardToAvailabilityCard(card)], {
        start: card.startDate,
        end: card.endDate,
      });
    }

    void urqlClient
      .mutation<UnplaceTeachingBlockCardsMutation, UnplaceTeachingBlockCardsMutationVariables>(
        UnplaceTeachingBlockCardsDocument,
        { cardUuids: [card.uuid] },
      )
      .toPromise();

    const newCard = {
      ...card,
      startDate: null,
      endDate: null,
    };

    this._updateCardArrays(newCard, 'discard');
    this.root.reset();
  }

  /**
   * Reduziert die AvailabilityCard auf die Ressourcen der **Zeilen-Achse des
   * aktuellen Grid-Kontexts**. Damit reagieren Snap/Auto-Fit nur auf Kollisionen
   * mit tatsächlichen Nachbarkarten in derselben Zeile (z. B. dieselbe Klasse) –
   * NICHT auf Ressourcen-Konflikte aus anderen Zeilen (Lehrer/Raum anderweitig
   * belegt). So bleibt ein „rotes Feld" platzierbar (der normale Konflikt-Dialog
   * fängt es ab); der Snap rastet nur an Karten der gleichen Zeile ein.
   *
   * **Klassenübergreifende Karten** behalten alle ihre Klassen/Teilungsgruppen –
   * eine Belegung in EINER der Klassen-Zeilen löst den Snap aus.
   */
  private _rowAxisAvailabilityCard(card: TeachingBlockCardType): AvailabilityCardType {
    const base = this.cardToAvailabilityCard(card);
    const empty: AvailabilityCardType = {
      ...base,
      classes: [],
      classDivisionGroups: [],
      teachers: [],
      rooms: [],
      subject: '',
    };
    switch (this.root.gridContext) {
      case 'classes':
        return { ...empty, classes: base.classes, classDivisionGroups: base.classDivisionGroups };
      case 'teachers':
        return { ...empty, teachers: base.teachers };
      case 'rooms':
        return { ...empty, rooms: base.rooms };
      case 'subjects':
        return { ...empty, subject: base.subject };
      default:
        return base;
    }
  }

  /**
   * Rastet den vorgeschlagenen Start auf die **getroffene Kalenderwoche**: Ein Klick ins
   * Board meint eine Woche, keinen einzelnen Tag – tag-genau platziert wird nur per Resize.
   * Ausgangspunkt ist deshalb immer der Montag der angeklickten Woche; von dort läuft der
   * Snap vorwärts über alle Tage, die als Start nicht taugen:
   *
   * - **Wochenenden** – eine Epoche beginnt an einem Schultag (Vorgänger endet Freitag →
   *   Start am Montag darauf, nicht am Samstag).
   * - **Ferientage** – die deckt der Split-Flow ab, sie sind aber kein Startkandidat.
   * - **belegte Tage der Zeilen-Achse** der Karte (s. {@link _rowAxisAvailabilityCard}) –
   *   liegt in der Woche schon eine Epoche, beginnt die neue am ersten Tag nach deren Ende
   *   (Vorgänger endet Mittwoch → Start Donnerstag), ohne Konflikt-Dialog.
   *
   * Koexistierende Karten (Wahlfächer) zählen dabei **nicht** als belegt – sie stapeln sich
   * auf demselben Zeitraum, statt sich gegenseitig nach hinten zu schieben (gleicher Filter
   * wie in {@link _fitEndBeforeNextCard}).
   *
   * Der Montag wird auf den Schuljahresbeginn geklemmt, damit der Rücksprung eine Karte nicht
   * vor den Schuljahresstart zieht (z. B. wenn das Schuljahr mitten in der Woche beginnt).
   * Bricht ab, sobald ein möglicher Starttag gefunden ist (oder nach einem Jahr als
   * Sicherheitsgrenze).
   */
  private _snapStartToWeek(card: TeachingBlockCardType, startDateString: string): string {
    const availabilityCard = this._rowAxisAvailabilityCard(card);
    const shouldCountPlacement = this.root.conflictStore.getCoexistFilterForCard(card);
    const schoolYearStart = dayjs(this.root.schoolYear.start).startOf('day');

    let start = dayjs(startDateString).startOf('isoWeek');
    if (start.isBefore(schoolYearStart, 'day')) start = schoolYearStart;

    for (let i = 0; i < 366; i++) {
      const weekday = start.day();
      const isWeekend = weekday === 0 || weekday === 6;
      const isHoliday = !!this.root.dates.get(start.format('YYYY-MM-DD'))?.holidayUuid;
      if (!isWeekend && !isHoliday) {
        const conflicts = this.root
          .getAvailabilityStore()
          ?.checkResourceConflicts([start.toDate()], availabilityCard, { shouldCountPlacement });
        if (!conflicts || conflicts.size === 0) break; // erster möglicher Starttag
      }
      start = start.add(1, 'day');
    }

    return start.format('YYYY-MM-DD');
  }

  /**
   * Scannt ab dem (bereits gesnappten, freien) Start die nominale Dauer und sucht
   * die erste **Folgekarte derselben Zeile** (aktueller Kontext, s.
   * {@link _rowAxisAvailabilityCard}). Liefert den letzten freien Tag davor als
   * mögliches Ende (Wochenenden zählen als belegbares Ende mit – so füllt eine
   * Karte vor einer Montags-Folgekarte das Wochenende und es entsteht keine Lücke)
   * sowie die Anzahl freier **Schultage** (Mo–Fr) bis dahin – nur die zählen zum
   * 4-Tage-Kürzungslimit. Ferientage stoppen den Scan nicht und sind kein
   * Endkandidat (Ferien werden separat über den Split-Flow behandelt).
   */
  private _fitEndBeforeNextCard(
    card: TeachingBlockCardType,
    startDateString: string,
  ): { conflictFound: boolean; endDateString: string; schoolDays: number } {
    const durationDays = this._getPlacementDays(card);
    const start = dayjs(startDateString).startOf('day');
    const availabilityCard = this._rowAxisAvailabilityCard(card);

    let lastFreeDay = start;
    let schoolDays = 0;
    let conflictFound = false;

    for (let i = 0; i < durationDays; i++) {
      const day = start.add(i, 'day');
      const isHoliday = !!this.root.dates.get(day.format('YYYY-MM-DD'))?.holidayUuid;
      if (isHoliday) continue; // Ferien: kein Endkandidat, Scan läuft weiter (Split übernimmt)

      // Wahlfächer stapeln sich, statt sich zu kürzen: eine andere Wahlfach-Karte ist
      // hier kein Endkandidat-Blocker. Für normale Karten liefert der Filter
      // `undefined` – das Auto-Fit-Verhalten bleibt dort unverändert.
      const conflicts = this.root.getAvailabilityStore()?.checkResourceConflicts([day.toDate()], availabilityCard, {
        shouldCountPlacement: this.root.conflictStore.getCoexistFilterForCard(card),
      });
      if (conflicts && conflicts.size > 0) {
        conflictFound = true;
        break;
      }

      // Wochenenden sind belegbare Endtage, zählen aber NICHT zum 4-Tage-Limit.
      lastFreeDay = day;
      const weekday = day.day();
      if (weekday !== 0 && weekday !== 6) schoolDays++;
    }

    return { conflictFound, endDateString: lastFreeDay.format('YYYY-MM-DD'), schoolDays };
  }

  /**
   * Tage, mit denen eine Karte platziert wird. Beim **Verschieben** einer bereits
   * platzierten (ggf. resizten) Karte (`startDate` gesetzt) bleibt die aktuelle
   * Länge erhalten; **frisch vom Stapel** (`startDate == null`, z. B. auch nach dem
   * Zurücklegen auf den Stapel) wird auf die Original-Epochenlänge zurückgesetzt.
   */
  private _getPlacementDays(card: TeachingBlockCardType): number {
    const original = (card.duration ?? 1) * 7;
    if (!card.startDate) return original;
    return card.durationInDays && card.durationInDays > 0 ? card.durationInDays : original;
  }

  public placeCard(
    card: TeachingBlockCardType,
    startDateString: string,
    pos?: { x: number; y: number },
    ignoreConflict?: boolean,
  ) {
    // Auto-Fit: Start auf den ersten möglichen Tag der getroffenen Woche rasten
    // (s. _snapStartToWeek) und – falls eine Folgekarte im Weg ist – das Ende auf den
    // letzten freien Schultag davor ziehen, statt sofort einen Konflikt-Dialog zu zeigen.
    // Beim bewussten „Konflikt ignorieren" (ignoreConflict) bleibt Start/Dauer unverändert;
    // der Dialog liefert dann den bereits gerasteten Start zurück.
    if (!ignoreConflict) {
      startDateString = this._snapStartToWeek(card, startDateString);

      const fit = this._fitEndBeforeNextCard(card, startDateString);
      if (fit.conflictFound) {
        const minSchoolDays = Math.max(1, (card.duration ?? 1) * 5 - 4);
        if (fit.schoolDays >= minSchoolDays) {
          // Lücke reicht → gekürzt platzieren (updateCard übernimmt DB, Verfügbarkeit
          // und Ferien-Split). Danach nächste Stapelkarte picken + Rückmeldung.
          void this.updateCard(card, startDateString, fit.endDateString, false, false).then(() => {
            this._pickNextStackCard(card, pos);
          });
          return;
        }
        // Würde die Mindestlänge unterschreiten → unten normaler Konflikt-Dialog.
      }
    }

    const daysInDuration: Map<string, GridDateType> = new Map();
    const duration = this._getPlacementDays(card);

    for (let i = 0; i < duration; i++) {
      const dateString = dayjs(startDateString).add(i, 'day').format('YYYY-MM-DD');
      const dateValue = this.root.dates.get(dateString);
      if (!dateValue) throw new Error('dateValue is undefined');
      daysInDuration.set(dateString, dateValue);
    }

    // find conflicts in resources (classes, groups, teachers, ...)
    const dates = [...daysInDuration.keys()].map((key) => dayjs(key).toDate());
    const conflicts = this.root
      .getAvailabilityStore()
      ?.checkResourceConflicts(dates, this.cardToAvailabilityCard(card), {
        shouldCountPlacement: this.root.conflictStore.getPlaceholderFilterForCard(card),
      });

    if (!ignoreConflict && conflicts && conflicts.size > 0) {
      this.root.setConflictModal({
        isOpen: true,
        card: card,
        startDateString: startDateString,
        conflicts: Array.from(conflicts.entries()).map(([resourceUuid, conflict]) => {
          return this.getWarning(card.uuid, conflict, resourceUuid);
        }),
      });
      return;
    }

    // find holidays in card's timespan
    const holidayUuids = uniqueBy(
      [...daysInDuration.values()].filter((day) => day.holidayUuid),
      'holidayUuid',
    ).map((h) => h.holidayUuid!);

    if (holidayUuids.length > 0) {
      const endDateString = [...daysInDuration.keys()][daysInDuration.size - 1];

      // Reicht die Epochenkarte über das Schuljahresende (31.07.) hinaus, lässt sich der
      // Konflikt nicht durch Verschieben lösen (kein freier Zeitraum dahinter). Statt des
      // nur abbrechbaren Dialogs das Karten-Ende automatisch auf den letzten Schuljahrestag
      // kürzen und die – ggf. verkürzte – Karte platzieren. Echte Ferien im verbleibenden
      // Zeitraum splittet updateCard → _findSplitsForCard weiterhin regulär mit.
      const schoolYearEndString = dayjs(this.root.schoolYear.end).startOf('day').format('YYYY-MM-DD');
      if (
        isFirstAfterSecond(endDateString, schoolYearEndString) &&
        !isFirstAfterSecond(startDateString, schoolYearEndString)
      ) {
        void this.updateCard(card, startDateString, schoolYearEndString, false, false).then(() => {
          this._pickNextStackCard(card, pos);
        });
        return;
      }

      this.root.timeConflictModal = {
        isOpen: true,
        card,
        start: dayjs(startDateString).startOf('day').toDate(),
        end: dayjs(endDateString).startOf('day').toDate(),
        startDateString,
        holidayUuids,
      };
      return;
    }

    this._placeCard(card, startDateString, pos);
  }

  /** Liegt im Zeitraum (inklusive) mindestens ein Ferientag? */
  private _rangeHasHoliday(startDateString: string, endDateString: string): boolean {
    const start = dayjs(startDateString).startOf('day');
    const days = getDaysDifference(startDateString, endDateString);
    for (let i = 0; i < days; i++) {
      if (this.root.dates.get(start.add(i, 'day').format('YYYY-MM-DD'))?.holidayUuid) return true;
    }
    return false;
  }

  public async updateCard(
    card: TeachingBlockCardType,
    startDateString: string,
    endDateString: string,
    includeHoliday = false,
    virtual = false,
  ) {
    // construct new card
    const newCard: TeachingBlockCardType & {
      startDate: Date;
      endDate: Date;
      includeHoliday: boolean;
      durationInDays: number;
    } = {
      ...card,
      startDate: dayjs(startDateString).startOf('day').toDate(),
      endDate: dayjs(endDateString).startOf('day').toDate(),
      includeHoliday,
      durationInDays: getDaysDifference(startDateString, endDateString),
    };

    // update virtual children
    const children = this.virtualCards.filter((vc) => vc.parentUuid === card.uuid);

    if (children.length > 0 || virtual) {
      const first = children.at(0);
      const last = [...children].pop();
      if (!first || !last) throw new Error('first or last is undefined');

      const cardStartString = dayjs(card.startDate).startOf('day').format('YYYY-MM-DD');
      const cardEndString = dayjs(card.endDate).startOf('day').format('YYYY-MM-DD');

      first.startDateString = startDateString !== cardStartString ? startDateString : cardStartString;
      first.duration = getDaysDifference(first.startDateString, first.endDateString);

      if (first.duration < 0) {
        this.virtualCards = this.virtualCards.filter((vc) => !children.map((c) => c.uuid).includes(vc.uuid));
      } else {
        const firstIndex = this.virtualCards.findIndex((c) => c.uuid === first.uuid);
        if (firstIndex === -1) {
          this.virtualCards.push(first);
        } else {
          this.virtualCards.splice(firstIndex, 1, first);
        }
      }

      last.endDateString = endDateString !== cardEndString ? endDateString : cardEndString;
      last.duration = getDaysDifference(last.startDateString, last.endDateString);

      if (last.duration < 0) {
        this.virtualCards = this.virtualCards.filter((vc) => !children.map((c) => c.uuid).includes(vc.uuid));
      } else {
        const lastIndex = this.virtualCards.findIndex((c) => c.uuid === last.uuid);
        if (lastIndex === -1) {
          this.virtualCards.push(last);
        } else {
          this.virtualCards.splice(lastIndex, 1, last);
        }
      }
    }

    // Alten Datumsbereich der Karte aus dem Availability-Store entfernen, bevor
    // der neue Bereich eingetragen wird – sonst läge die Karte gleichzeitig im
    // alten und neuen Zeitraum (Phantom-Konflikte). `card` trägt noch den alten
    // Stand (start/endDate), `newCard` den neuen.
    if (card.startDate && card.endDate) {
      this.root.getAvailabilityStore()?.removeCardsResourcesPlaced([this.cardToAvailabilityCard(card)], {
        start: card.startDate,
        end: card.endDate,
      });
    }

    this.root.getAvailabilityStore()?.setCardsResourcesPlaced([this.cardToAvailabilityCard(card)], {
      start: newCard.startDate,
      end: newCard.endDate,
    });

    // Eine geteilte Karte bleibt nur geteilt, solange in ihrem neuen Zeitraum noch Ferien liegen.
    // Passt man sie z. B. per Doppelklick in die Lücke eines Teilstücks ein, liegt keine Ferienzeit
    // mehr darin: dann ist sie wieder eine normale gesteckte Karte. Vorher blieb sie als „geteilt“
    // markiert, hatte aber kein passendes Teilstück mehr und verschwand vom Board.
    const staysSplit =
      (children.length > 0 || virtual) && (includeHoliday || this._rangeHasHoliday(startDateString, endDateString));

    runInAction(() => {
      if (staysSplit) {
        this._updateCardArrays(newCard, 'virtual');
      } else {
        this.virtualCards = this.virtualCards.filter((vc) => vc.parentUuid !== card.uuid);
        this._updateCardArrays(newCard, 'update');
      }

      this.freeCards = this.freeCards.filter((c) => c.uuid !== card.uuid);

      // Statt globalem _loadCards (Splits + Verfügbarkeit ALLER Karten + setContext)
      // nur die geänderte Karte auf Ferien-Splits prüfen. Verfügbarkeit/Arrays wurden
      // oben bereits inkrementell aktualisiert.
      this._findSplitsForCard(newCard);
    });

    // Persistenz nach dem lokalen Update starten, damit Resize/Modal-Änderungen
    // ohne sichtbaren Rücksprung direkt im Grid bleiben.
    await urqlClient
      .mutation<PlaceTeachingBlockCardMutation, PlaceTeachingBlockCardMutationVariables>(
        PlaceTeachingBlockCardDocument,
        {
          cardUuid: newCard.uuid,
          // Reines Kalenderdatum (lokaler Tag der Karte), keine Zeitzonen-Umrechnung.
          startDate: dayjs(newCard.startDate).format('YYYY-MM-DD'),
          endDate: dayjs(newCard.endDate).format('YYYY-MM-DD'),
          includeHoliday: newCard.includeHoliday,
        },
      )
      .toPromise();
  }

  public splitTimeConflict(card?: TeachingBlockCardType, startDateString?: string, save = true) {
    const cardToUse = this.root.timeConflictModal.card ?? card;
    const startStringToUse = this.root.timeConflictModal.startDateString ?? startDateString;
    const endStringToUse = this.root.timeConflictModal.endDateString;
    if (!cardToUse || !startStringToUse) throw new Error('timeConflict data is undefined');

    if (endStringToUse) {
      // Resize-Fall: über den gezogenen Bereich [start, end] splitten. Modal ZUERST
      // zurücksetzen, sonst rekursiert das automatische Re-Split in updateCard
      // (_findSplitsForCard → splitTimeConflict) erneut in diesen Zweig. updateCard
      // (includeHoliday=false) splittet dann via durationInDays (= Bereich) um die Ferien.
      this.root.resetTimeConflict();
      void this.updateCard(cardToUse, startStringToUse, endStringToUse, false, false);
      return;
    }

    runInAction(() => {
      this.virtualCards = this.virtualCards.filter((vc) => vc.parentUuid !== cardToUse.uuid);
    });

    // Ohne Speichern (Ferien-Splits beim Laden, s. `_findSplits`) laufen viele Splits
    // hintereinander an. Jeder einzelne würde in `_updateCardArrays` die Konflikte ALLER
    // Karten neu berechnen – bei einem großen Plan fror das Board dadurch minutenlang ein.
    // Deshalb zählen: erst der letzte ausstehende Split baut die Konflikte neu auf.
    if (!save) this.#pendingUnsavedSplits++;

    this._splitCard(cardToUse, startStringToUse, !save)
      .then(() => {
        const children = this.virtualCards.filter((vc) => vc.parentUuid === cardToUse.uuid);
        const first = children && children.length > 0 ? children[0] : undefined;
        const last = children && children.length > 0 ? children[children.length - 1] : undefined;
        if (!first || !last) throw new Error('first or last child is undefined');
        if (save) {
          this.updateCard(cardToUse, first.startDateString, last.endDateString, false, true);
        } else {
          this.#pendingUnsavedSplits--;
          this._updateCardArrays(cardToUse, 'virtual', this.#pendingUnsavedSplits === 0);
        }
      })
      .catch((error: unknown) => {
        // Der Zähler darf nicht hängen bleiben, sonst bliebe der Konflikt-Neuaufbau aus.
        if (!save) {
          this.#pendingUnsavedSplits = Math.max(0, this.#pendingUnsavedSplits - 1);
          if (this.#pendingUnsavedSplits === 0) this.root.conflictStore.setConflicts();
        }
        throw error;
      });

    this.root.resetTimeConflict();
  }

  // ─── Konflikte / Warnungen ────────────────────────────────────────────────

  /**
   * Wandelt einen einzelnen Ressourcen-Konflikt in eine Anzeige-Warnung um.
   * Wird sowohl für den Platzierungs-Dialog (`placeCard` → conflictModal) als
   * auch für die persistente Anzeige (`getWarningsForCard`) genutzt.
   */
  public getWarning(cardUuid: string, conflict: AvailabilityDayType, conflictUuid: string) {
    const { type, blocked, maybe } = conflict;
    const card = this._cards.find((c) => c.uuid === cardUuid);

    let warningType: WarningType = 'overlappingClass';
    let name: string = '';

    switch (type) {
      case 'teacher': {
        warningType = blocked ? 'teacherNotAvailable' : maybe ? 'teacherMaybe' : 'overlappingTeachers';
        name = card?.teachers.find((t) => t.uuid === conflictUuid)?.listName ?? '';
        break;
      }
      case 'room': {
        warningType = blocked ? 'roomNotAvailable' : maybe ? 'roomMaybe' : 'overlappingRooms';
        // TODO: find room with name
        break;
      }
      case 'class': {
        warningType = blocked ? 'classNotAvailable' : maybe ? 'classMaybe' : 'overlappingClass';
        name = card?.classes.find((c) => c.uuid === conflictUuid)?.name ?? '';
        break;
      }
      case 'divisionGroup': {
        warningType = 'overlappingGroups';
        name = card?.groups.find((g) => g.uuid === conflictUuid)?.name ?? '';
        break;
      }
      default:
        break;
    }

    return {
      type: warningType,
      data: [
        {
          name,
          uuid: conflictUuid,
        },
      ],
    };
  }

  // ─── Ferien-Splitting ─────────────────────────────────────────────────────

  private _findSplits() {
    this.getRows().forEach((row) => this._findSplitsInRow(row));
    this.root.resetTimeConflict();
  }

  /**
   * Prüft NUR die übergebene Karte auf Ferien im Zeitraum und splittet sie ggf.
   * Ersetzt in {@link updateCard} den globalen {@link _loadCards}-Reload: Resize/Auto-Fit
   * ändern nur diese eine Karte, daher ist ein Voll-Reload (Splits + Verfügbarkeit ALLER
   * Karten + `setContext`) unnötig teuer. Logik identisch zu {@link _findSplitsInRow},
   * nur auf eine Karte beschränkt.
   */
  private _findSplitsForCard(card: TeachingBlockCardType) {
    if (card.includeHoliday || !card.startDate || !card.durationInDays) return;

    const startDateString = dayjs(card.startDate).format('YYYY-MM-DD');
    for (let i = 0; i < card.durationInDays; i++) {
      const dateString = dayjs(startDateString).add(i, 'day').format('YYYY-MM-DD');
      if (this.root.dates.get(dateString)?.holidayUuid) {
        this.splitTimeConflict(card, startDateString, false);
        return;
      }
    }
  }

  private _findSplitsInRow(row: RowItem | undefined) {
    if (!row) throw new Error('row is undefined');
    const cards = this.placedCards.filter((card) => card.lessonClasses.some((lc) => lc.class.uuid === row.value));

    cards.forEach((card) => {
      if (card.includeHoliday) return;

      const daysInDuration: Map<string, GridDateType> = new Map();
      const startDateString = dayjs(card.startDate).format('YYYY-MM-DD');

      for (let i = 0; i < card.durationInDays; i++) {
        const dateString = dayjs(startDateString).add(i, 'day').format('YYYY-MM-DD');
        const dateValue = this.root.dates.get(dateString);
        if (!dateValue) throw new Error('dateValue is undefined');
        daysInDuration.set(dateString, dateValue);
      }

      const holidayUuids = uniqueBy(
        [...daysInDuration.values()].filter((day) => day.holidayUuid),
        'holidayUuid',
      ).map((h) => h.holidayUuid!);

      if (holidayUuids.length > 0) {
        this.splitTimeConflict(card, startDateString, false);
      }
    });
  }

  private async _splitCard(
    card: TeachingBlockCardType,
    startDateString: string,
    skipHolidays: boolean,
    remainder?: number,
  ) {
    const childCount = this.virtualCards.filter((vc) => vc.parentUuid === card.uuid).length ?? 0;
    const duration = remainder ?? (childCount === 0 && skipHolidays ? card.durationInDays : (card.duration ?? 1) * 7);

    let holiday: HolidayType | null = null;
    let dayCounter: number = 0;
    let holidayCounter: number = 0;

    for (let i = 0; i < duration; i++) {
      const dateString = dayjs(startDateString).add(i, 'day').format('YYYY-MM-DD');
      const day = this.root.dates.get(dateString);
      if (day?.holidayUuid) {
        if (!holiday) holiday = this.root.holidays.get(day?.holidayUuid) ?? null;
        if (childCount === 0 && skipHolidays) holidayCounter++;
        if (dayCounter === 0) dayCounter = i;
      }
    }

    dayCounter = holiday ? dayCounter : duration;

    const endDateString = holiday
      ? dayjs(holiday.start).subtract(1, 'day').format('YYYY-MM-DD')
      : dayjs(startDateString).add(dayCounter, 'day').subtract(1, 'day').format('YYYY-MM-DD');

    const virtualCard: TeachingBlockVirtualCard = {
      uuid: `${card.uuid}#${childCount}`,
      parentUuid: card.uuid,
      startDateString,
      endDateString,
      duration: dayCounter,
    };

    this.virtualCards.push(virtualCard);

    if (duration - dayCounter - holidayCounter > 0) {
      this._splitCard(
        card,
        holiday ? dayjs(holiday.end).add(1, 'day').format('YYYY-MM-DD') : startDateString,
        skipHolidays,
        duration - dayCounter - holidayCounter,
      );
    }
  }

  public _placeCard(card: TeachingBlockCardType, startDateString: string, pos?: { x: number; y: number }) {
    const daysInDuration: Map<string, GridDateType> = new Map();
    const duration = this._getPlacementDays(card);

    for (let i = 0; i < duration; i++) {
      const dateString = dayjs(startDateString).add(i, 'day').format('YYYY-MM-DD');
      const dateValue = this.root.dates.get(dateString);
      if (!dateValue) throw new Error('dateValue is undefined');
      daysInDuration.set(dateString, dateValue);
    }

    const endDateString = [...daysInDuration.keys()][daysInDuration.size - 1];

    const newCard: PlacedTeachingBlockCardType = {
      ...card,
      startDate: dayjs(startDateString).startOf('day').toDate(),
      endDate: dayjs(endDateString).startOf('day').toDate(),
      includeHoliday: true,
      durationInDays: duration,
    };

    void urqlClient
      .mutation<PlaceTeachingBlockCardMutation, PlaceTeachingBlockCardMutationVariables>(
        PlaceTeachingBlockCardDocument,
        {
          cardUuid: card.uuid,
          // Reines Kalenderdatum (lokaler Tag der Karte), keine Zeitzonen-Umrechnung.
          startDate: dayjs(newCard.startDate).format('YYYY-MM-DD'),
          endDate: dayjs(newCard.endDate).format('YYYY-MM-DD'),
          includeHoliday: newCard.includeHoliday ?? false,
        },
      )
      .toPromise();

    this.root.getAvailabilityStore()?.setCardsResourcesPlaced([this.cardToAvailabilityCard(card)], {
      start: newCard.startDate,
      end: newCard.endDate,
    });

    this._updateCardArrays(newCard, 'place');

    this._pickNextStackCard(card, pos);
  }

  /** Nach dem Platzieren die nächste freie Karte desselben Stapels picken (oder nichts). */
  private _pickNextStackCard(card: TeachingBlockCardType, pos?: { x: number; y: number }) {
    const nextCard = this.freeCards.find((freeCard) => {
      return (
        freeCard.uuid !== card.uuid &&
        freeCard.lesson?.uuid === this.root.pickedCard?.card.lesson?.uuid &&
        freeCard.duration === this.root.pickedCard?.card.duration
      );
    });

    nextCard ? this.root.pickCard(nextCard, { x: pos?.x ?? 0, y: pos?.y ?? 0 }, true) : this.root.pickCard(null);
  }

  /**
   * @param rebuildConflicts false, wenn der Aufrufer mehrere Karten nacheinander ändert und die
   *   Konflikte erst am Ende einmal neu aufbaut (s. `splitTimeConflict`).
   */
  private _updateCardArrays(
    card: TeachingBlockCardType | PlacedTeachingBlockCardType,
    operation: 'place' | 'discard' | 'update' | 'virtual',
    rebuildConflicts = true,
  ) {
    this._cards = this._cards.filter((c) => c.uuid !== card.uuid);
    this._cards.push(card);
    this._indexCards();

    runInAction(() => {
      switch (operation) {
        case 'place':
          {
            const index = this.placedCards.findIndex((c) => c.uuid === card?.uuid);
            if (index === -1) {
              this.placedCards.push(card as PlacedTeachingBlockCardType);
            } else {
              this.placedCards.splice(index, 1, card as PlacedTeachingBlockCardType);
            }
            this.freeCards = this.freeCards.filter((c) => c.uuid !== card.uuid);
            this.virtualCards = this.virtualCards.filter((vc) => vc.parentUuid !== card.uuid);
          }
          break;

        case 'discard':
          {
            const index = this.freeCards.findIndex((c) => c.uuid === card?.uuid);
            if (index === -1) {
              this.freeCards.push(card);
            } else {
              this.freeCards.splice(index, 1, card);
            }
            this.placedCards = this.placedCards.filter((c) => c.uuid !== card.uuid);
            this.virtualCards = this.virtualCards.filter((vc) => vc.parentUuid !== card.uuid);
          }
          break;

        case 'update':
          {
            const index = this.placedCards.findIndex((c) => c.uuid === card?.uuid);
            if (index === -1) {
              if (card.startDate && card.endDate) {
                this.placedCards.push(card as PlacedTeachingBlockCardType);
              }
            } else {
              this.placedCards.splice(index, 1, card as PlacedTeachingBlockCardType);
            }
            this.freeCards = this.freeCards.filter((c) => c.uuid !== card.uuid);
          }
          break;

        case 'virtual':
          this.placedCards = this.placedCards.filter((c) => c.uuid !== card.uuid);
          break;
        default:
          break;
      }

      if (rebuildConflicts) this.root.conflictStore.setConflicts();
    });
  }

  // ─── Räume ────────────────────────────────────────────────────────────────

  public async setRoomsForCards(
    cards: Array<PlacedTeachingBlockCardType | TeachingBlockCardType>,
    roomsToSet: Array<{ __typename?: 'Room'; name: string; uuid: string }>,
    keepCurrent: boolean = false,
  ) {
    const promises: Promise<unknown>[] = [];

    const newCards = cards.map((card) => {
      const newCardRooms = keepCurrent ? [...card.rooms, ...roomsToSet] : [...roomsToSet];

      promises.push(
        urqlClient
          .mutation<SetTeachingBlockCardRoomsMutation, SetTeachingBlockCardRoomsMutationVariables>(
            SetTeachingBlockCardRoomsDocument,
            // jeder Raum einmal: mit keepCurrent kann ein Raum schon an der Karte hängen
            { cardUuid: card.uuid, roomUuids: [...new Set(newCardRooms.map((r) => r.uuid))] },
          )
          .toPromise(),
      );

      return { ...card, rooms: newCardRooms };
    });

    void Promise.all(promises);

    runInAction(() => {
      newCards.forEach((card) => {
        const isPlaced = this.placedCards.some((pc) => pc.uuid === card.uuid);
        const updatedCard: PlacedTeachingBlockCardType | TeachingBlockCardType = {
          ...card,
          rooms: card.rooms.map((r) => ({ __typename: 'Room', uuid: r.uuid })),
        };
        if (isPlaced) {
          const index = this.placedCards.findIndex((pc) => pc.uuid === card.uuid);
          if (index !== -1) {
            if (updatedCard.startDate && updatedCard.endDate) {
              this.placedCards[index] = updatedCard as PlacedTeachingBlockCardType;
            }
          }
        } else {
          const index = this.freeCards.findIndex((fc) => fc.uuid === card.uuid);
          if (index !== -1) {
            this.freeCards[index] = updatedCard;
          }
        }

        // Auch die Roh-Karte (`_cards`/`_cardsByUuid`) aktualisieren: gesplittete Eltern
        // fehlen in `placedCards`/`freeCards`, ihre Raum-Zeilen-Zuordnung (VirtualGridCard →
        // getParentCard → getRowValuesForCard(parent.rooms)) liest aber aus `_cardsByUuid`.
        // Ohne dieses Update bliebe die Zuweisung bei Ferien-Karten unsichtbar.
        const rawIndex = this._cards.findIndex((c) => c.uuid === updatedCard.uuid);
        if (rawIndex !== -1) {
          this._cards[rawIndex] = updatedCard;
        }

        this.mapRoomsForCard(updatedCard);
      });

      this._indexCards();
    });
  }

  public async removeRoomsFromCards(
    cards: Array<PlacedTeachingBlockCardType | TeachingBlockCardType>,
    roomsToRemove: Array<{ __typename?: 'Room'; name: string; uuid: string }>,
    removeAll: boolean = false,
  ) {
    const newCards = cards.map((card) => {
      const newRooms = removeAll
        ? []
        : card.rooms.filter((room) => {
            return !roomsToRemove.some((r) => r.uuid === room.uuid);
          });

      return { ...card, rooms: newRooms };
    });
    await this.setRoomsForCards(newCards, [], true);
  }

  public mapRoomsForCard(card: TeachingBlockCardType) {
    const roomSupply: Map<RoomTypes, RoomItem[]> = new Map();
    const assignedRooms = card.rooms.map(({ uuid }) => this._mapRoom(uuid, 'assigned')) ?? [];
    const lessonRooms =
      card.lesson?.roomSupplyConnection.edges.map(({ node }) => this._mapRoom(node.uuid, 'lesson')) ?? [];
    const subjectRooms = card.subject?.defaultRooms.map(({ uuid }) => this._mapRoom(uuid, 'subject')) ?? [];
    const teacherRooms =
      card.teachers
        ?.filter(({ defaultRoom }) => defaultRoom)
        .map(({ defaultRoom }) => this._mapRoom(defaultRoom?.uuid ?? '', 'teacher')) ?? [];
    const classRooms = card.classes
      ?.filter((c) => c?.defaultRoom)
      .map((c) => this._mapRoom(c?.defaultRoom?.uuid ?? '', 'class'));
    const schoolRooms = this.root.rawRooms.filter((r) => r.classroom).map((r) => this._mapRoom(r.uuid, 'school')) ?? [];

    const allRooms = [
      ...assignedRooms,
      ...lessonRooms,
      ...subjectRooms,
      ...teacherRooms,
      ...classRooms,
      ...schoolRooms,
    ];

    const uniqueRooms = uniqueBy<RoomItem>(
      allRooms.sort((a, b) => a.label.localeCompare(b.label)),
      'value',
    );

    roomSupply.set(
      'assigned',
      uniqueRooms.filter((ur) => ur.group === 'assigned'),
    );

    roomSupply.set(
      'lesson',
      uniqueRooms.filter((ur) => ur.group === 'lesson'),
    );

    roomSupply.set(
      'subject',
      uniqueRooms.filter((ur) => ur.group === 'subject'),
    );

    roomSupply.set(
      'teacher',
      uniqueRooms.filter((ur) => ur.group === 'teacher'),
    );

    roomSupply.set(
      'class',
      uniqueRooms.filter((ur) => ur.group === 'class'),
    );

    roomSupply.set(
      'school',
      uniqueRooms.filter((ur) => ur.group === 'school'),
    );

    runInAction(() => {
      this.cardRooms.set(card.uuid, roomSupply);
    });
  }

  private _mapRoom(roomUuid: string, group: RoomTypes): RoomItem {
    const room = this.root.rawRooms.find((r) => r.uuid === roomUuid);
    if (!room) throw new Error('room not found');
    return { label: room.name, value: room.uuid, group, buildingName: room.building?.name };
  }

  // ─── Lookups ──────────────────────────────────────────────────────────────

  public getCardsInRow(row: RowItem | Item): TeachingBlockCardType[] {
    return this.placedCards.filter((card) => {
      return this.isUuidPresentInTimetableCard(card, row.value);
    });
  }

  public isUuidPresentInTimetableCard(card: TeachingBlockCardType, uuid: string): boolean {
    if (this.root.gridContext === 'classes') {
      return card.lessonClasses?.some((c) => c.class.uuid === uuid) ?? false;
    }
    if (this.root.gridContext === 'teachers') {
      return card.teachers?.some((c) => c.uuid === uuid) ?? false;
    }
    if (this.root.gridContext === 'rooms') {
      return card.rooms?.some((r) => r.uuid === uuid) ?? false;
    }
    return false;
  }

  public isUuidPresentInVirtualCard(card: TeachingBlockVirtualCard, uuid: string): boolean {
    const parent = this._cardsByUuid.get(card.parentUuid);
    if (!parent) throw new Error('parent is undefined');
    return this.isUuidPresentInTimetableCard(parent, uuid);
  }

  public getParentCard(uuid: string) {
    return this._cardsByUuid.get(uuid);
  }

  /**
   * Alle auf dem Board sichtbaren platzierten Karten: die direkt platzierten
   * ({@link placedCards}) **plus** die durch einen Ferien-Split zu virtuellen Segmenten
   * gewordenen Eltern. Letztere fehlen in {@link placedCards} (dort per `_updateCardArrays`
   * case `'virtual'` entfernt) und existieren nur noch in {@link virtualCards} – ohne sie
   * würden Bulk-Aktionen (alle sperren/entsperren/entfernen) und der Raum-Picker die
   * gesplitteten Karten übergehen.
   */
  public get allPlacedCards(): PlacedTeachingBlockCardType[] {
    const virtualParents = [...new Set(this.virtualCards.map((vc) => vc.parentUuid))]
      .map((uuid) => this.getParentCard(uuid))
      .filter((card): card is PlacedTeachingBlockCardType => !!card);
    return [...this.placedCards, ...virtualParents];
  }

  /**
   * Zeilen-Werte (`row.value`) einer Karte im **aktuellen** Grid-Kontext – also die
   * Zeilen, in denen die Karte erscheint. Single Source of Truth für die
   * Row-Week-Indizes (Fix B) und `rowsToHighlight` (Fix C). Konsistent zu
   * {@link isUuidPresentInTimetableCard}.
   */
  public getRowValuesForCard(card: TeachingBlockCardType): string[] {
    if (this.root.gridContext === 'classes') {
      return [...new Set(card.lessonClasses?.map((lc) => lc.class.uuid) ?? [])];
    }
    if (this.root.gridContext === 'teachers') {
      return [...new Set(card.teachers?.map((teacher) => teacher.uuid) ?? [])];
    }
    if (this.root.gridContext === 'rooms') {
      return [...new Set(card.rooms?.map((room) => room.uuid) ?? [])];
    }
    return [];
  }

  /**
   * Platzierte Karten je `${row.value}:${weekNumber}:${year}` (Start-Woche der
   * Karte). MobX-`computed`: cacht und rechnet nur neu, wenn sich `placedCards`
   * oder `gridContext` ändern – **nicht** beim Picken. Ersetzt den O(n)-Filter pro
   * `GridWeek` durch O(1)-Lookup (Fix B).
   */
  public get placedCardsByRowWeek(): Map<string, PlacedTeachingBlockCardType[]> {
    const map = new Map<string, PlacedTeachingBlockCardType[]>();
    this.placedCards.forEach((card) => {
      const start = dayjs(card.startDate).startOf('day');
      // ISO-KW + ISO-Wochenjahr (vgl. TeachingBlockStore._initGrid): konsistent mit
      // dem Grid und korrekt in Jahren mit 53 Wochen. `isoWeekYear` ist für alle
      // sieben Tage einer Woche identisch und vermeidet so Jahres-Mismatch am
      // Jahreswechsel (Karte landet in der richtigen GridWeek-Zelle).
      const week = start.isoWeek();
      const year = start.isoWeekYear();
      this.getRowValuesForCard(card).forEach((rowValue) => {
        const key = `${rowValue}:${week}:${year}`;
        const bucket = map.get(key);
        if (bucket) bucket.push(card);
        else map.set(key, [card]);
      });
    });
    return map;
  }

  /** Kalendertage, die eine platzierte Karte belegt (inklusive Start und Ende). */
  private _daysOfCard(card: PlacedTeachingBlockCardType): string[] {
    const start = dayjs(card.startDate).startOf('day');
    const end = dayjs(card.endDate).startOf('day');
    const days: string[] = [];
    for (let i = 0, count = Math.max(0, end.diff(start, 'day')); i <= count; i++) {
      days.push(start.add(i, 'day').format('YYYY-MM-DD'));
    }
    return days;
  }

  /**
   * Stapel-Layout der Wahlfach-Karten je `${row.value}:${card.uuid}`.
   *
   * Wahlfächer verdrängen sich nicht, sondern teilen sich die Zeilenhöhe: pro Zeile
   * bekommen alle elective-Karten per Greedy-Coloring über ihre Kalendertage eine Spur
   * (`track`) zugewiesen – die erste Spur, die an keinem ihrer Tage schon belegt ist.
   *
   * `divisions` (= Nenner der Höhe) wird bewusst **je Karte aus ihrem vollsten eigenen
   * Tag** bestimmt, nicht aus der Anzahl der Karten in der Zeile: liegen drei Karten so,
   * dass an keinem Tag mehr als zwei gleichzeitig laufen, bekommt jede 1/2 Höhe statt 1/3.
   * `track + 1` ist dabei die Untergrenze, damit eine Karte immer in ihre eigene Spur passt.
   *
   * Nur tatsächlich gestapelte Karten (`divisions > 1`) landen in der Map. Eine einzelne
   * elective-Karte behält damit ihre normale, aus der Klassenteilung abgeleitete Höhe
   * (`cssHeight`/`cssTop`); erst beim Stapeln überschreibt das Coexist-Layout diese.
   *
   * MobX-`computed`: rechnet nur bei Änderung von `placedCards`/`gridContext` neu.
   */
  public get coexistLayoutByRow(): Map<string, { track: number; divisions: number }> {
    const layout = new Map<string, { track: number; divisions: number }>();

    const cardsByRow = new Map<string, PlacedTeachingBlockCardType[]>();
    this.placedCards.forEach((card) => {
      if (!isCoexistableTeachingBlockCard(card)) return;
      this.getRowValuesForCard(card).forEach((rowValue) => {
        const bucket = cardsByRow.get(rowValue);
        if (bucket) bucket.push(card);
        else cardsByRow.set(rowValue, [card]);
      });
    });

    cardsByRow.forEach((cards, rowValue) => {
      if (cards.length < 2) return; // allein → kann sich mit niemandem stapeln

      // Reihenfolge NUR nach der unveränderlichen uuid – bewusst nicht nach Start/Dauer.
      // Diese ändern sich beim Resize, sodass die geänderte Karte sonst in der Sortierung
      // nach vorne rutscht und ihre Spur (oben/unten) springt. Die uuid ist stabil, damit
      // bleibt die Spur einer Karte über Resize/Verschieben hinweg gleich.
      const ordered = [...cards].sort((a, b) => a.uuid.localeCompare(b.uuid));

      const daysByCard = new Map<string, string[]>();
      const tracksByDay = new Map<string, Set<number>>();
      const trackByCard = new Map<string, number>();

      ordered.forEach((card) => {
        const days = this._daysOfCard(card);
        daysByCard.set(card.uuid, days);

        let track = 0;
        while (days.some((day) => tracksByDay.get(day)?.has(track))) track++;
        trackByCard.set(card.uuid, track);

        days.forEach((day) => {
          const taken = tracksByDay.get(day) ?? new Set<number>();
          tracksByDay.set(day, taken.add(track));
        });
      });

      ordered.forEach((card) => {
        const track = trackByCard.get(card.uuid) ?? 0;
        const divisions = (daysByCard.get(card.uuid) ?? []).reduce(
          (max, day) => Math.max(max, tracksByDay.get(day)?.size ?? 1),
          track + 1,
        );
        if (divisions > 1) layout.set(`${rowValue}:${card.uuid}`, { track, divisions });
      });
    });

    return layout;
  }

  /** Stapel-Layout einer Karte in einer Zeile, oder `undefined` wenn sie nicht gestapelt ist. */
  public getCoexistLayout(rowValue: string, cardUuid: string): { track: number; divisions: number } | undefined {
    return this.coexistLayoutByRow.get(`${rowValue}:${cardUuid}`);
  }

  /** VirtualCards je `${row.value}:${weekNumber}:${year}` (Start-Woche). Vgl. {@link placedCardsByRowWeek}. */
  public get virtualCardsByRowWeek(): Map<string, TeachingBlockVirtualCard[]> {
    const map = new Map<string, TeachingBlockVirtualCard[]>();
    this.virtualCards.forEach((virtualCard) => {
      const parent = this._cardsByUuid.get(virtualCard.parentUuid);
      if (!parent) return;
      const start = dayjs(virtualCard.startDateString).startOf('day');
      // ISO-KW + ISO-Wochenjahr, identisch zu placedCardsByRowWeek (KW-53-fest).
      const week = start.isoWeek();
      const year = start.isoWeekYear();
      this.getRowValuesForCard(parent).forEach((rowValue) => {
        const key = `${rowValue}:${week}:${year}`;
        const bucket = map.get(key);
        if (bucket) bucket.push(virtualCard);
        else map.set(key, [virtualCard]);
      });
    });
    return map;
  }

  /**
   * Fein-granulare Sicht auf {@link placedCardsByRowWeek} für genau eine `${row}:${week}:${year}`.
   * Per-Key gecachte `computed` mit `comparer.shallow`: ändert sich `placedCards`, rechnet zwar
   * die zugrundeliegende Map neu, aber nur die Row-Weeks, deren Karten-Liste sich wirklich
   * ändert, benachrichtigen ihre Observer (GridWeek) → kein Full-Grid-Rerender beim Ablegen.
   */
  public placedCardsForRowWeek(indexKey: string): PlacedTeachingBlockCardType[] {
    let cached = this.#placedCardsForRowWeekCache.get(indexKey);
    if (!cached) {
      cached = computed(() => this.placedCardsByRowWeek.get(indexKey) ?? EMPTY_PLACED_CARDS, {
        equals: comparer.shallow,
      });
      this.#placedCardsForRowWeekCache.set(indexKey, cached);
    }
    return cached.get();
  }

  /** Wie {@link placedCardsForRowWeek}, aber für die VirtualCards. */
  public virtualCardsForRowWeek(indexKey: string): TeachingBlockVirtualCard[] {
    let cached = this.#virtualCardsForRowWeekCache.get(indexKey);
    if (!cached) {
      cached = computed(() => this.virtualCardsByRowWeek.get(indexKey) ?? EMPTY_VIRTUAL_CARDS, {
        equals: comparer.shallow,
      });
      this.#virtualCardsForRowWeekCache.set(indexKey, cached);
    }
    return cached.get();
  }

  public getNextCard(card: TeachingBlockCardType): TeachingBlockCardType | null {
    const rowValues = new Set(this.getRowValuesForCard(card));
    const rows = this.getRows().filter((row) => rowValues.has(row.value));

    const placedCardsInRowsAfterCurrent = this._cards
      .filter((c) => {
        return (
          c.startDate && c.uuid !== card.uuid && rows.some((row) => this.isUuidPresentInTimetableCard(c, row.value))
        );
      })
      .sort((a, b) => (isFirstBeforeSecond(a.startDate, b.startDate) ? -1 : 1))
      .filter((placed) => {
        return (
          isFirstBeforeSecond(card.endDate, placed.startDate) ||
          isFirstBetweenSecondAndThird(card.endDate, placed.startDate, placed.endDate)
        );
      });

    const cardWithoutDivision = card.lessonClasses.every((c) => {
      return !c.usedDivision;
    });

    return (
      placedCardsInRowsAfterCurrent.find((placed) => {
        // Wahlfächer dürfen sich überlagern → ein koexistierbarer Nachbar ist keine
        // Resize-Grenze für eine koexistierbare Karte. Sonst behandelt die Bounds-Logik
        // die gestapelte Partnerkarte (gleiche Tage) als direkten Nachbarn und klemmt den
        // Resize komplett (minDate > maxDate → getCardResizeBounds liefert null).
        if (isCoexistableTeachingBlockCard(card) && isCoexistableTeachingBlockCard(placed)) {
          return false;
        }
        const placedWithoutDivision = placed.lessonClasses.every((c) => {
          return c.usedDivision === null;
        });
        if (cardWithoutDivision || placedWithoutDivision) {
          return placed;
        }
        return card.lessonClasses.every((lc) => {
          const matchingLessonClass = placed.lessonClasses.find((plc) => {
            return plc.class.uuid === lc.class.uuid;
          });
          return !(
            matchingLessonClass &&
            matchingLessonClass.usedDivision?.uuid === lc.usedDivision?.uuid &&
            card.groups.every((g) => {
              return placed.groups.some((pg) => pg.uuid !== g.uuid);
            })
          );
        });
      }) ?? null
    );
  }

  public getNextHoliday(card: TeachingBlockCardType): HolidayType | null {
    return (
      this.root.rawHolidays
        .sort((a, b) => (isFirstBeforeSecond(a.start, b.start) ? -1 : 1))
        .find((holiday) => isFirstAfterSecond(holiday.start, card.endDate)) ?? null
    );
  }

  public getPreviousCard(card: TeachingBlockCardType): TeachingBlockCardType | null {
    const rowValues = new Set(this.getRowValuesForCard(card));
    const rows = this.getRows().filter((row) => rowValues.has(row.value));

    const placedCardsInRowsBeforeCurrent = this._cards
      .filter((c) => {
        return (
          c.startDate && c.uuid !== card.uuid && rows.some((row) => this.isUuidPresentInTimetableCard(c, row.value))
        );
      })
      .sort((a, b) => (isFirstBeforeSecond(a.startDate, b.startDate) ? 1 : -1))
      .filter((placed) => {
        return (
          isFirstBeforeSecond(placed.endDate, card.startDate) ||
          isFirstBetweenSecondAndThird(card.startDate, placed.startDate, placed.endDate)
        );
      });

    const cardWithoutDivision = card.lessonClasses.every((c) => {
      return !c.usedDivision;
    });

    return (
      placedCardsInRowsBeforeCurrent.find((placed) => {
        // s. getNextCard: koexistierbare Nachbarn begrenzen den Resize nicht.
        if (isCoexistableTeachingBlockCard(card) && isCoexistableTeachingBlockCard(placed)) {
          return false;
        }
        const placedWithoutDivision = placed.lessonClasses.every((c) => {
          return c.usedDivision === null;
        });
        if (cardWithoutDivision || placedWithoutDivision) {
          return placed;
        }
        return card.lessonClasses.every((lc) => {
          const matchingLessonClass = placed.lessonClasses.find((plc) => {
            return plc.class.uuid === lc.class.uuid;
          });
          return !(
            matchingLessonClass &&
            matchingLessonClass.usedDivision?.uuid === lc.usedDivision?.uuid &&
            card.groups.every((g) => {
              return placed.groups.some((pg) => pg.uuid !== g.uuid);
            })
          );
        });
      }) ?? null
    );
  }

  public getPreviousHoliday(card: TeachingBlockCardType): HolidayType | null {
    return (
      this.root.rawHolidays
        .sort((a, b) => (isFirstBeforeSecond(a.start, b.start) ? 1 : -1))
        .find((holiday) => isFirstBeforeSecond(holiday.end, card.startDate)) ?? null
    );
  }

  /**
   * Abweichung der aktuellen Kartenlänge von der Original-Epochenlänge in **Tagen**
   * (positiv = länger, negativ = kürzer). Ferientage im Zeitraum zählen NICHT mit –
   * eine Karte, die nur wegen überspannter Ferien länger im Kalender steht, gilt
   * nicht als abweichend. 0 = keine Abweichung (oder Karte nicht platziert).
   */
  public getCardLengthDeviationDays(card: TeachingBlockCardType): number {
    if (!card.startDate || card.durationInDays == null) return 0;
    const original = (card.duration ?? 1) * 7;
    let holidayDays = 0;
    for (let i = 0; i < card.durationInDays; i++) {
      const dateString = dayjs(card.startDate).add(i, 'day').format('YYYY-MM-DD');
      if (this.root.dates.get(dateString)?.holidayUuid) holidayDays++;
    }
    return card.durationInDays - holidayDays - original;
  }

  // ─── Computed ─────────────────────────────────────────────────────────────

  public get availableCards() {
    if (this.root.gridContext === 'rooms') {
      return this.placedCards.filter((c) => c.rooms.length === 0);
    }
    return this.root.selectedRows.length > 0
      ? this.freeCards.filter((card) => {
          return this.root.selectedRows.some((row) => {
            return this.isUuidPresentInTimetableCard(card, row.value);
          });
        })
      : this.freeCards;
  }

  public get selectedRowCards() {
    return this.placedCards.filter((card) => {
      return this.root.selectedRows.some((row) => this.isUuidPresentInTimetableCard(card, row.value));
    });
  }
}
